import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('change', this.syncInstallmentsVariantId);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('change', this.syncInstallmentsVariantId);
  }

  /**
   * Immediately syncs the installments product form's hidden "id" input the moment a
   * variant option is picked, before the asynchronous section refresh completes. Checkout
   * apps read the first /cart/add form's "id" field at click time, so it must already hold
   * the selected variant when the shopper taps a buy/checkout button.
   * @param {Event} event - The change event from a variant option input.
   */
  syncInstallmentsVariantId = (event) => {
    const target = event.target;
    let variantId;
    if (target instanceof HTMLSelectElement) {
      variantId = target.selectedOptions[0]?.dataset.variantId;
    } else if (target instanceof HTMLElement) {
      variantId = target.dataset.variantId;
    }
    if (!variantId) return;

    const input = this.querySelector('.installments input[name="id"]');
    if (input instanceof HTMLInputElement) input.value = variantId;
  };

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    // Keep the installments product form's hidden variant id in sync with the selected
    // variant. This input lives outside [ref="priceContainer"], so it is not refreshed by
    // the price swap above. It must track the selection because it is the product form's
    // "id" field submitted to /cart/add.
    const newVariantId = event.detail.data.html.querySelector('product-price .installments input[name="id"]');
    const currentVariantId = this.querySelector('.installments input[name="id"]');
    if (newVariantId instanceof HTMLInputElement && currentVariantId instanceof HTMLInputElement) {
      currentVariantId.value = newVariantId.value;
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
